/**
 * Utilitaires pour la gestion des statuts et des formatages
 */
import { Appointment } from '../types';

/**
 * Obtient la classe CSS pour la couleur du statut d'un rendez-vous
 * @param status - Statut du rendez-vous
 * @returns Classe CSS correspondant au statut
 */
export const getStatusColor = (status: Appointment['status']): string => {
  switch (status) {
    case 'confirmed':
      return 'status-confirmed';
    case 'cancelled':
      return 'status-cancelled';
    case 'completed':
      return 'status-completed';
    default:
      return 'status-pending';
  }
};

/**
 * Obtient le libellé traduit pour le statut d'un rendez-vous
 * @param status - Statut du rendez-vous
 * @returns Libellé traduit du statut
 */
export const getStatusLabel = (status: Appointment['status']): string => {
  switch (status) {
    case 'confirmed':
      return 'Confirmé';
    case 'cancelled':
      return 'Annulé';
    case 'completed':
      return 'Terminé';
    default:
      return 'En attente';
  }
};

/**
 * Obtient la classe CSS pour la couleur du statut d'email
 * @param status - Statut de l'email
 * @returns Classe CSS correspondant au statut
 */
export const getEmailStatusColor = (status: string | undefined): string => {
  if (!status || status === 'not_sent') return 'status-not-sent';
  
  switch (status) {
    case 'sending':
      return 'status-sending';
    case 'sent':
    case 'delivered':
      return 'status-sent';
    case 'opened':
    case 'clicked':
      return 'status-opened';
    case 'bounced':
    case 'failed':
      return 'status-failed';
    default:
      return 'status-not-sent';
  }
};

/**
 * Obtient le libellé traduit pour le statut d'email
 * @param status - Statut de l'email
 * @returns Libellé traduit du statut
 */
export const getEmailStatusLabel = (status: string | undefined): string => {
  if (!status || status === 'not_sent') return 'Non envoyé';
  
  switch (status) {
    case 'sending':
      return 'En cours d\'envoi';
    case 'sent':
      return 'Envoyé';
    case 'delivered':
      return 'Livré';
    case 'opened':
      return 'Ouvert';
    case 'clicked':
      return 'Cliqué';
    case 'bounced':
      return 'Rejeté';
    case 'failed':
      return 'Échec';
    default:
      return status;
  }
};

/**
 * Obtient la classe CSS pour la couleur du statut de SMS
 * @param status - Statut du SMS
 * @returns Classe CSS correspondant au statut
 */
export const getSMSStatusColor = (status: string | undefined): string => {
  if (!status || status === 'not_scheduled') return 'status-not-sent';
  
  switch (status) {
    case 'scheduled':
      return 'status-scheduled';
    case 'sending':
      return 'status-sending';
    case 'sent':
      return 'status-sent';
    case 'delivered':
      return 'status-delivered';
    case 'failed':
      return 'status-failed';
    default:
      return 'status-not-sent';
  }
};

/**
 * Obtient le libellé traduit pour le statut de SMS
 * @param status - Statut du SMS
 * @returns Libellé traduit du statut
 */
export const getSMSStatusLabel = (status: string | undefined): string => {
  if (!status || status === 'not_scheduled') return 'Non programmé';
  
  switch (status) {
    case 'scheduled':
      return 'Programmé';
    case 'sending':
      return 'En cours d\'envoi';
    case 'sent':
      return 'Envoyé';
    case 'delivered':
      return 'Livré';
    case 'failed':
      return 'Échec';
    default:
      return status;
  }
};

/**
 * Formate une date au format JJ-MM-AAAA
 * @param dateString - Chaîne de date à formater
 * @returns Date formatée
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error('Erreur de formatage de date:', error);
    return dateString;
  }
};

/**
 * Formate une heure au format HH:MM
 * @param timeString - Chaîne d'heure à formater
 * @returns Heure formatée
 */
export const formatTime = (timeString: string): string => {
  if (!timeString) return '';
  
  // Si l'heure est déjà au format HH:MM, la retourner telle quelle
  if (/^\d{2}:\d{2}$/.test(timeString)) {
    return timeString;
  }
  
  // Si l'heure est au format HH:MM:SS, retourner seulement HH:MM
  if (/^\d{2}:\d{2}:\d{2}$/.test(timeString)) {
    return timeString.substring(0, 5);
  }
  
  return timeString;
};

// --- Fonctions pour Statuts Simplifiés et Pastilles ---

// Type pour les statuts simplifiés
export type SimplifiedEmailStatus = 'Envoyé' | 'Erreur' | 'Non envoyé';
export type SimplifiedSMSStatus = 'Programmé' | 'Envoyé' | 'Erreur' | 'Non programmé';

// Simplification du statut Email
export const getSimplifiedEmailStatus = (rawStatus: string | null | undefined): SimplifiedEmailStatus => {
  if (!rawStatus || rawStatus === 'not_sent') {
    return 'Non envoyé';
  }
  switch (rawStatus) {
    case 'sent':
    case 'delivered':
    case 'opened':
    case 'clicked':
      return 'Envoyé';
    case 'bounced':
    case 'failed':
      return 'Erreur';
    case 'sending': // Considéré comme non finalisé -> Non envoyé pour la pastille? Ou Erreur? Disons 'Non envoyé' pour le moment
    default:
      return 'Non envoyé'; // Ou 'Erreur' si on considère l'inconnu comme tel?
  }
};

// Simplification du statut SMS
export const getSimplifiedSMSStatus = (rawStatus: string | null | undefined): SimplifiedSMSStatus => {
  if (!rawStatus || rawStatus === 'not_scheduled') {
    return 'Non programmé';
  }
  switch (rawStatus) {
    case 'scheduled':
      return 'Programmé';
    case 'sent':
    case 'delivered': // Sera 'Envoyé' quand le webhook fonctionnera
      return 'Envoyé';
    case 'failed':
      return 'Erreur';
    default:
      return 'Non programmé'; // Ou 'Erreur'?
  }
};

// Classe CSS pour la pastille Email
export const getEmailPastilleClass = (simplifiedStatus: SimplifiedEmailStatus): string => {
  switch (simplifiedStatus) {
    case 'Envoyé':
      return 'pastille-success'; // Vert
    case 'Erreur':
      return 'pastille-error';   // Rouge
    case 'Non envoyé':
    default:
      return 'pastille-neutral'; // Gris
  }
};

// Classe CSS pour la pastille SMS
export const getSMSPastilleClass = (simplifiedStatus: SimplifiedSMSStatus): string => {
  switch (simplifiedStatus) {
    case 'Programmé':
      return 'pastille-warning'; // Orange/Jaune
    case 'Envoyé':
      return 'pastille-success'; // Vert
    case 'Erreur':
      return 'pastille-error';   // Rouge
    case 'Non programmé':
    default:
      return 'pastille-neutral'; // Gris
  }
};

// --- Fin Fonctions pour Statuts Simplifiés et Pastilles ---
